import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  setLogVerbosity,
} from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import type { PreloadQueryFunction } from '@apollo/client/react';
import { ApolloProvider, createQueryPreloader } from '@apollo/client/react';
import type { Consumer } from '@rails/actioncable';
import { createConsumer } from '@rails/actioncable';
import * as Sentry from '@sentry/core';
import { Kind, OperationTypeNode } from 'graphql';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { RouteObject } from 'react-router';
import { finalize, map } from 'rxjs';

import {
  API_URL,
  HOSTNAME,
  IS_PLAYGROUND,
  IS_TEST_RUNNER,
  WS_ROOT,
  isProduction,
} from '#src/config.ts';
import { DebugProvider } from '#src/contexts/debug/Provider.tsx';
import { useDeviceFingerprintContext } from '#src/contexts/deviceFingerprint/index.ts';
import { Level } from '#src/contexts/snackNotification/index.ts';
import { OperationStoreClient } from '#src/gql/OperationStoreClient.ts';
import introspectionResult from '#src/gql/introspectionResult.json';
import { useDebugLink } from '#src/hooks/graphql/useDebugLink.ts';
import { useTMLink } from '#src/hooks/graphql/useTMLink.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { notificationsQueue } from '#src/hooks/useNotificationsQueue.ts';
import { loadCookie, saveCookie } from '#src/lib/cookies.ts';
import { sendSafeError } from '#src/lib/error.ts';
import { GraphQLNetworkError } from '#src/lib/fingerprintedErrors.ts';
import { inFlightOperations } from '#src/lib/gql/inFlightOperations.ts';
import { initializeOptimizeQuery } from '#src/lib/gql/optimizeQuery.ts';
import {
  defaultHeaders,
  xsrfCookieName,
  xsrfReceivingHeaderName,
} from '#src/lib/http.ts';
import { isAbortedRequestError } from '#src/lib/http/isAbortedRequestError.ts';
import { isNoInternetConnectionError } from '#src/lib/http/isNoInternetConnectionError.ts';
import { isStaleWebAppVersionError } from '#src/lib/http/isStaleWebAppVersionError.ts';
import { networkErrorFingerprint } from '#src/lib/http/networkErrorFingerprint.ts';
import { getPathPattern } from '#src/lib/routing/getPathPattern.ts';
import { getAnalyticsHeaders } from '#src/lib/sessionHeaders.ts';

import { ActionCableLink } from './ActionCableLink.ts';
import { GraphqlContext } from './index.tsx';
import { typePolicies } from './typePolicies.ts';

const headersToObject = (headers: HeadersInit | undefined) => {
  if (!headers) return {};
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return headers;
};

const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const xsrfToken = loadCookie(xsrfCookieName);
  const headers = {
    ...defaultHeaders,
    ...getAnalyticsHeaders(),
    ...headersToObject(options.headers),
    ...(xsrfToken && typeof xsrfToken === 'string'
      ? { 'X-CSRF-Token': xsrfToken }
      : {}),
  };

  return fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });
};

type ServerErrorBody = {
  message?: string;
  maintenance?: { start?: string; end?: string; msg?: string };
};

const parseServerErrorBody = (error: ServerError) => {
  try {
    return JSON.parse(error.bodyText) as ServerErrorBody;
  } catch {
    return undefined;
  }
};

const cable = () => createConsumer(WS_ROOT);

const MAX_RETRIES = 8;
const INITIAL_RETRY_DELAY = 1000;

export const shouldRetryOperation: RetryLink.AttemptsFunction = (
  count,
  operation,
  error
) => {
  if (operation.operationType === OperationTypeNode.MUTATION) {
    return false;
  }

  if (
    typeof (error as ServerError)?.statusCode === 'number' ||
    isAbortedRequestError(error)
  ) {
    return false;
  }

  return !!error && count < MAX_RETRIES;
};

if (import.meta.env.MODE === 'development') {
  setLogVerbosity('debug');
}

const createCache = () =>
  new InMemoryCache({
    possibleTypes: introspectionResult.possibleTypes,
    typePolicies: IS_PLAYGROUND ? undefined : typePolicies,
  });

export const coreCache = createCache();

const hasSubscriptionOperation = ({ query }: ApolloLink.Operation) =>
  query.definitions.some(
    definition =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.SUBSCRIPTION
  );

const inFlightOperationsLink = new ApolloLink((operation, forward) => {
  // Subscriptions stay open for the component's lifetime, so counting them
  // would mean the client is never idle.
  if (hasSubscriptionOperation(operation)) {
    return forward(operation);
  }

  inFlightOperations.current += 1;
  return forward(operation).pipe(
    finalize(() => {
      inFlightOperations.current -= 1;
    })
  );
});

type Props = {
  children: ReactNode;
  uri?: string;
  basename?: string;
  routeObjects?: RouteObject[];
  disableGraphQLErrorsReport?: boolean;
  locale: string;
};

// allow access to the client from outside react context
// used for react-router data route loaders https://reactrouter.com/en/main/route/loader
const apolloClient: {
  current: ApolloClient | null;
} = {
  current: null,
};
export const preloadQuery: {
  current: PreloadQueryFunction | null;
} = {
  current: null,
};

// According to the Apollo documentation (https://www.apollographql.com/docs/react/v2/data/error-handling/)
// once you define an ErrorLink, you will be triggered whatever the `errorPolicy` that is specified in the query
// and the only way for you to prevent the GraphQLError from being processed is to condition its processing
// based on the operation name.
const SILENCE_GQL_ERRORS_FROM_OPERATION_NAMES = [
  // ignore errors for when the joinSecret is invalid
  'DialogLeagueQuery',
  'ChartQuery',
];

// Sentry scrubs long variable values, so a "too long" validation error or a 413
// loses the only thing that explains it: which variable overflowed, and by how much.
const LONG_VARIABLE_THRESHOLD = 100;
const variableLength = (value: unknown) =>
  typeof value === 'string'
    ? value.length
    : (JSON.stringify(value)?.length ?? 0);
export const longVariableLengths = (variables: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(variables)
      .map(([key, value]) => [key, variableLength(value)] as const)
      .filter(([, length]) => length > LONG_VARIABLE_THRESHOLD)
  );

const isTooManySubscriptionsError = (err: { message: string }) =>
  err.message === 'Too many subscriptions from the same user, IP or channel.';

// The backend validator (platform.validators.expired_exchange_rate) returns a
// bare localized message with no extensions.code, so match the known
// translations. Stopgap until the backend attaches a stable error code.
const EXPIRED_EXCHANGE_RATE_MESSAGES = new Set(
  [
    'exchange rate has expired',
    'wisselkoers is verlopen',
    'le taux de change a expiré',
    'Wechselkurs nicht mehr gültig',
    'il tasso di cambio è scaduto',
    'taxa de câmbio expirou',
    'обменный курс устарел',
    'el tipo de cambio ha caducado',
    'kur değerinin süresi sona erdi',
  ].map(message => message.toLowerCase())
);
export const isExpiredExchangeRateError = (err: { message: string }) =>
  EXPIRED_EXCHANGE_RATE_MESSAGES.has(err.message.trim().toLowerCase());

// GraphQL-Ruby rejects variables referencing a field that was removed from an
// input type with "Variable $x of type Y was provided invalid value for z
// (Field is not defined on Y)". Only stale clients (old cached bundles built
// against a previous schema) send such fields, so there is nothing actionable
// to report. Other coercion problems (e.g. "(Expected ... to be a key-value
// object.)") are real bugs and must keep being reported, hence the check that
// every problem is a missing-field one.
export const isRemovedInputFieldError = (err: { message: string }) => {
  if (
    !/^Variable \$\w+ of type \S+ was provided invalid value/.test(err.message)
  ) {
    return false;
  }
  const problems = err.message.match(/\([^)]*\)/g) ?? [];
  return (
    problems.length > 0 &&
    problems.every(problem => /^\(Field is not defined on \w+\)$/.test(problem))
  );
};

function getOperationNameAndId(body: BodyInit | null | undefined) {
  try {
    const { operationName, extensions: { operationId } = {} } = JSON.parse(
      body as string
    ) as {
      operationName: string;
      extensions?: { operationId?: string };
    };
    return { operationName, operationId };
  } catch {
    return { operationName: '', operationId: undefined };
  }
}

export const createErrorAndRetryLink = ({
  disableGraphQLErrorsReport,
  isMobileApp,
}: {
  disableGraphQLErrorsReport: boolean;
  isMobileApp: boolean;
}) => {
  const onErrorLink = new ErrorLink(({ error: linkError, operation }) => {
    // v4 hands the handler a single `error`: CombinedGraphQLErrors for GraphQL
    // failures, the transport error itself otherwise.
    const graphQLErrors = CombinedGraphQLErrors.is(linkError)
      ? linkError.errors
      : undefined;
    const networkError = graphQLErrors ? undefined : linkError;

    if (networkError) {
      if (ServerError.is(networkError)) {
        // In the case of 429 network error for signIn, we make this error silent cause we want to have
        // an inline error instead of a notification: (error is handled at a lower level in the form directly)
        if (
          networkError.statusCode === 429 &&
          operation?.operationName === 'SignInMutation'
        ) {
          return undefined;
        }

        // In the case of 500 network error, do not report to Sentry because the backend has already
        if (networkError.statusCode === 500) {
          notificationsQueue.addNotification('errors', {
            errors: [networkError.message],
          });
          return undefined;
        }

        // The API asking for a reload, which `EnsureLatestAppVersion`
        // performs — not a failure.
        if (isStaleWebAppVersionError(networkError)) {
          return undefined;
        }

        if (networkError.statusCode === 503) {
          const { start, end, msg } =
            parseServerErrorBody(networkError)?.maintenance || {};

          if (msg) {
            notificationsQueue.addNotification('serviceUnderMaintenance', {
              start,
              end,
              msg,
            });
            return undefined;
          }
        }
      }

      // A request the client itself cancelled (navigation, unmount, an
      // AbortController firing) never had an answer to report.
      if (isAbortedRequestError(networkError)) {
        return undefined;
      }

      if (isNoInternetConnectionError(networkError)) {
        // skip the snack notification on mobile apps: they already have native handling
        if (!isMobileApp) {
          notificationsQueue.addNotification(
            'lostInternetConnection',
            undefined,
            {
              level: Level.ERROR,
              ignoreDuplicates: true,
            }
          );
        }

        // A connection the client lost is not actionable, and an API outage
        // reaches us as a 5xx or through backend monitoring instead.
        return undefined;
      }

      notificationsQueue.addNotification('errors', {
        errors: [networkError.message],
      });

      const message = ServerError.is(networkError)
        ? parseServerErrorBody(networkError)?.message || networkError.bodyText
        : networkError.message;

      if (!disableGraphQLErrorsReport && message) {
        const error = `NetworkError: "${message}" in ${operation.operationName}`;

        Sentry.addBreadcrumb({ level: 'debug', data: { networkError } });
        Sentry.withScope(scope => {
          scope.setTag('kind', operation.operationName);
          scope.setExtra(
            'longVariableLengths',
            longVariableLengths(operation.variables)
          );
          scope.setFingerprint(networkErrorFingerprint(networkError));
          sendSafeError(
            new GraphQLNetworkError(error, { cause: networkError })
          );
        });
      }

      return undefined;
    }

    if (
      graphQLErrors &&
      !SILENCE_GQL_ERRORS_FROM_OPERATION_NAMES.includes(
        operation.operationName ?? ''
      )
    ) {
      if (
        graphQLErrors.find(e => e.extensions?.code === 'UNAUTHORIZED') &&
        !IS_PLAYGROUND &&
        !IS_TEST_RUNNER
      ) {
        // session has expired, redirect to login page the hard way as we're too high in the provider stack
        // to rely on the Config/CurrentUser/Connection contexts
        // oxlint-disable-next-line no-restricted-properties
        window.location.replace(
          `/?action=signin&redirectUrl=${encodeURIComponent(
            // oxlint-disable-next-line no-restricted-properties
            `${window.location.pathname}${window.location.search}`
          )}`
        );
        // eslint-disable-next-line no-console
        console.error(
          `Session has expired, redirecting to login page:\n ${graphQLErrors.map(e => `\t${e.message}`).join('\n')}`
        );
        return undefined;
      }

      if (IS_TEST_RUNNER) {
        // eslint-disable-next-line no-console
        console.error(
          'GRAPHQL_ERROR',
          operation.operationName,
          graphQLErrors.map(e => e.message).join('\n')
        );
      }
      const tooManySubscriptions = graphQLErrors.some(
        isTooManySubscriptionsError
      );
      const reportableErrors = graphQLErrors.filter(
        err => !isTooManySubscriptionsError(err)
      );

      if (tooManySubscriptions) {
        notificationsQueue.addNotification('tooManySubscriptions', undefined, {
          level: Level.WARN,
          ignoreDuplicates: true,
        });
      }

      if (reportableErrors.length) {
        notificationsQueue.addNotification('errors', {
          errors: reportableErrors.map(e => e.message),
        });
      }

      if (!disableGraphQLErrorsReport && reportableErrors.length) {
        Sentry.addBreadcrumb({ level: 'debug', data: { graphQLErrors } });
        Sentry.withScope(scope => {
          scope.setTag('kind', operation.operationName);
          scope.setExtra('variables', operation.variables);
          scope.setExtra(
            'longVariableLengths',
            longVariableLengths(operation.variables)
          );
          reportableErrors.forEach(err => {
            if (err.extensions?.code === 'NOT_FOUND') {
              // do not report "not found" errors
              return;
            }

            // Recoverable: the settlement hooks refresh the rate and retry,
            // and report themselves if that retry is also rejected.
            if (isExpiredExchangeRateError(err)) {
              return;
            }

            if (isRemovedInputFieldError(err)) {
              return;
            }

            const error = `GraphqlError: "${err.message}" in ${
              operation.operationName
            } > ${(err.path ?? []).join(' > ')})`;

            sendSafeError(new Error(error, { cause: err }));
          });
        });
      }
    }

    return undefined;
  });

  const retryLink = new RetryLink({
    delay: {
      initial: INITIAL_RETRY_DELAY,
      jitter: true,
      max: Infinity,
    },
    attempts: shouldRetryOperation,
  });

  return ApolloLink.from([onErrorLink, retryLink]);
};

export const StaticLocaleGraphqlProvider = ({
  children,
  uri = API_URL,
  disableGraphQLErrorsReport = false,
  locale,
  basename,
  routeObjects,
}: Props) => {
  const { isMobileApp } = useIsMobileApp();

  const envApiKey =
    (import.meta.env.MODE === 'development' &&
      isProduction &&
      import.meta.env.VITE_SORARE_COM_API_KEY) ||
    undefined;

  const apiKey = useRef<string | undefined>(envApiKey);
  const setApiKey = (s: string | undefined) => {
    apiKey.current = s;
  };

  const deviceFingerprint = useRef<string>(undefined);
  const { deviceFingerprint: generateDeviceFingerprint } =
    useDeviceFingerprintContext();

  const [wsCable, setWsCable] = useState<Consumer>(cable());
  const activeQueries = useRef(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const debugLink = useDebugLink();
  const tmLink = useTMLink({ path: new URL(uri).pathname });

  const link = useMemo(() => {
    const errorAndRetryLink = createErrorAndRetryLink({
      disableGraphQLErrorsReport,
      isMobileApp,
    });

    const httpLink = ApolloLink.split(
      hasSubscriptionOperation,
      new ActionCableLink({ cable: wsCable }),
      // oxlint-disable-next-line react/refs
      new HttpLink({
        uri,
        fetchOptions: {
          referrerPolicy: 'unsafe-url',
        },
        headers: {
          accept: 'application/json',
          'Accept-Language': locale,
        },
        fetch: async (
          requestUri: URL | RequestInfo,
          options: RequestInit = {}
        ) => {
          const { operationName, operationId } = getOperationNameAndId(
            options.body
          );

          activeQueries.current += 1;
          if (!deviceFingerprint.current) {
            deviceFingerprint.current = await generateDeviceFingerprint();
          }
          const pathPattern =
            routeObjects &&
            getPathPattern(
              routeObjects,
              // oxlint-disable-next-line no-restricted-properties
              window.location.pathname,
              basename
            );
          options.headers = {
            DEVICE_FINGERPRINT: deviceFingerprint.current,
            ...headersToObject(options.headers),
            ...(apiKey.current && { APIKEY: apiKey.current }),
            ...(pathPattern ? { SORARE_PATH_PATTERN: pathPattern } : {}),
          };

          const { method } = options;
          return Sentry.startSpan(
            {
              // Do not create a transaction for this span
              // Only record it if it has a parent span (page-load / navigation)
              onlyIfParent: true,
              op: 'http.client',
              name: `${method} ${requestUri} ${[operationName, operationId]
                .filter(Boolean)
                .join(' ')}`,
            },
            async span => {
              return Sentry.suppressTracing(async () =>
                authenticatedFetch(requestUri as string, options)
              ).then(response => {
                Sentry.setHttpStatus(span, response.status);
                return response;
              });
            }
          )
            .then(response => {
              const contentType = response.headers.get('content-type');
              if (
                contentType &&
                !contentType.includes('application/json') &&
                response.status !== 500
              ) {
                response
                  // We need to clone the request to avoid
                  // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#locked_and_disturbed_streams
                  .clone()
                  .text()
                  .then(body => {
                    sendSafeError(
                      new Error(
                        `Invalid content type ${contentType} for response ${body}`
                      )
                    );
                  });
              }
              return response;
            })
            .finally(() => {
              activeQueries.current -= 1;
            });
        },
        credentials: 'include',
      })
    );

    const afterwareLink = new ApolloLink((operation, forward) => {
      return forward(operation).pipe(
        map(response => {
          const ctx = operation.getContext();
          if (!ctx.response) return response;

          const {
            response: { headers },
          } = ctx;
          if (headers?.get(xsrfReceivingHeaderName)) {
            const expires = new Date();
            expires.setFullYear(expires.getFullYear() + 1);

            saveCookie(xsrfCookieName, headers.get(xsrfReceivingHeaderName), {
              path: '/',
              // `*.wt.localhost` shares the session, so CSRF must share too.
              domain: HOSTNAME.endsWith('.wt.localhost')
                ? 'wt.localhost'
                : HOSTNAME.replace(/.*\.sorare\./, 'sorare.'),
              expires,
            });
          }
          if (
            import.meta.env.MODE === 'development' &&
            headers?.get('X-bullet-footer-text')
          ) {
            notificationsQueue.addNotification(
              'errors',
              {
                errors: JSON.parse(headers?.get('X-bullet-footer-text')),
              },
              {
                level: Level.ERROR,
              }
            );
          }

          return response;
        })
      );
    });

    const operationStoreApolloLink = OperationStoreClient()?.apolloLink;
    const operationStoreLink =
      operationStoreApolloLink && new ApolloLink(operationStoreApolloLink);

    return ApolloLink.from(
      [
        inFlightOperationsLink,
        debugLink,
        tmLink,
        afterwareLink,
        operationStoreLink,
        errorAndRetryLink,
        httpLink,
      ].filter(Boolean)
    );
  }, [
    wsCable,
    uri,
    locale,
    debugLink,
    tmLink,
    disableGraphQLErrorsReport,
    isMobileApp,
    generateDeviceFingerprint,
    basename,
    routeObjects,
    apiKey,
    deviceFingerprint,
  ]);

  const client = useMemo(() => {
    return new ApolloClient({
      link,
      cache: coreCache,
      assumeImmutableResults: true,

      devtools: {
        enabled: !IS_TEST_RUNNER,
      },

      defaultOptions: {
        mutate: {
          // workaround for https://github.com/apollographql/apollo-client/issues/10081
          update: () => {},
        },
      },
    });
  }, [link]);
  // oxlint-disable-next-line react/immutability
  apolloClient.current = client;
  // oxlint-disable-next-line react/immutability
  preloadQuery.current = createQueryPreloader(client);
  if (apolloClient.current && !isInitialized) {
    // only render children when the client is initialized
    // to avoid undefined apolloClient in route loaders
    initializeOptimizeQuery(() => {
      setIsInitialized(true);
    });
  }

  const refreshWsCable = useCallback(() => {
    setWsCable(prev => {
      prev.disconnect();
      return cable();
    });
    return null;
  }, [setWsCable]);

  return (
    <GraphqlContext
      value={{
        refreshWsCable,
        setApiKey,
        activeQueries,
      }}
    >
      <ApolloProvider client={client}>
        {!!isInitialized && children}
      </ApolloProvider>
    </GraphqlContext>
  );
};

export const GraphqlProvider = (props: Props) => {
  return (
    <DebugProvider>
      <StaticLocaleGraphqlProvider {...props} />
    </DebugProvider>
  );
};
